/**
 * This is the Geoapify API (https://www.geoapify.com/),
 * which can geocode addresses to gps locations, and vice versa
 */
import store from '@/store';
import queryString from 'query-string';

// const apiKey = 'f038ba1722b544c7b746999f61abcea6';

/**
 * @brief Get the Geoapify API key
 * @returns API key
 */
function getApiKey() {
  const geoApifyInfo = store.getters['Account/geoApifyInfo'];
  let apiKey = '';

  if (geoApifyInfo != null && geoApifyInfo.ApiKey != null) {
    apiKey = geoApifyInfo.ApiKey;
  }

  return apiKey;
}

/**
 * @brief Get the full url for forward geocoding of an address
 * @param {object} address Address to be geocoded, part of the query params
 * @returns {string} Full url for GET request
 */
function getForwardGeocodingUrl(address) {
  const apiKey = getApiKey();
  const urlInit = `https://api.geoapify.com/v1/geocode/search?apiKey=${apiKey}`;
  const queryParamsObj = {
    limit: 1, // Maximal number of results
    type: 'postcode', // Geocode postal code
  };

  // Get user language. Default is Dutch. 2-character ISO 639-1 language code
  let languageCode = 'nl';
  const userLanguage = store.getters['auth/userLanguage'];
  if (userLanguage != null && userLanguage.EnglishName != null) {
    switch (userLanguage.EnglishName) {
      case 'English':
        languageCode = 'en';
        break;
      case 'German':
        languageCode = 'de';
        break;
      default:
        break;
    }
  }

  queryParamsObj.lang = languageCode;

  // Geoapify API doesn't allow empty inputs for the params
  if (address.Nr != null && address.Nr.trim() !== '') {
    queryParamsObj.housenumber = address.Nr;
  }
  // if (address.Street != null && address.Street.trim() !== '') {
  //   queryParamsObj.street = address.Street;
  // }
  if (address.PostalCode != null && address.PostalCode.trim() !== '') {
    const postalCodeNoSpaces = address.PostalCode.replace(/\s+/g, '');
    queryParamsObj.postcode = postalCodeNoSpaces;
  }
  // if (address.City != null && address.City.trim() !== '') {
  //   queryParamsObj.city = address.City;
  // }
  if (address.Country != null && address.Country.trim() !== '') {
    queryParamsObj.country = address.Country;
  }

  const queryParamsStr = queryString.stringify(queryParamsObj);

  return `${urlInit}&${queryParamsStr}`;
}

/**
 * @brief Get the response from fetch() method
 * @param {object} response Response object from fetch() method
 * @returns {promise} a promise which resolves with the result of parsing the body text
 */
function getBodyAndStatus(response) {
  return response.json().then((responceBody) => ({
    status: response.status,
    body: responceBody,
  }));
}

/**
 * @brief Update Latitude and Longitude when geocoding finished successfully
 * @param {object} queryResult geocoded result
 * @param {object} address original address to be geocoded
 * @returns {object} address object which contains updated Latitude and Longitude
 */
function updateCoordinatesInAddress(queryResult, address) {
  if (queryResult == null || queryResult.features == null || queryResult.features.length === 0) {
    return address;
  }

  const addressUpdate = { ...address };
  const [feature] = queryResult.features;
  const properties = { ...feature.properties };

  if (properties != null) {
    addressUpdate.City = properties.city ? properties.city : '';
    addressUpdate.Street = properties.street ? properties.street : '';
    addressUpdate.Confident = false;
    if (properties.lat != null) {
      addressUpdate.Latitude = properties.lat;
    }
    if (properties.lon != null) {
      addressUpdate.Longitude = properties.lon;
    }

    /*
    About geocoding confidence
    - confidence: indicates if the complete address is correct
    - confidence_street_level: indicates if the street is correct
    - confidence_city_level: indicates if the city is correct
    */
    if (properties.rank.confidence === 1
      && properties.rank.confidence_city_level === 1
      && properties.rank.confidence_street_level === 1
    ) {
      addressUpdate.Confident = true;
    }
  }

  return addressUpdate;
}

function logResult(request, result) {
  console.groupCollapsed(`%cThe call [${request}] on [Geoapify API] was successful`,
    'background-color: #81ad61; color: white; border-radius: 25px; padding: 0 5px');
  console.log('Query result: ', result);
  console.groupEnd();
}

function logError(request, error) {
  console.groupCollapsed(`%cThe call [${request}] on [Geoapify API] was not successful`,
    'background-color: #f3584d; color: white; border-radius: 25px; padding: 0 5px');
  console.log('Query error: ', error);
  console.groupEnd();
}

/**
 * @brief Search request to geocode an address
 * @param {object} address address to be geocoded
 * @param {function} successCallback callback function on success
 * @param {function} errorCallback callback function on error
 */
function forwardGeocoding(address, successCallback, errorCallback) {
  if (address == null || Object.keys(address).length === 0) {
    errorCallback(address);
    return;
  }

  const url = getForwardGeocodingUrl(address);

  fetch(url)
    .then(getBodyAndStatus)
    .then((result) => {
      if (result.status === 200) {
        logResult('forwardGeocoding', result);
        const addressUpdate = updateCoordinatesInAddress(result.body, address);
        successCallback(addressUpdate);
      } else {
        logError('forwardGeocoding', result);
        errorCallback(result);
      }
    })
    .catch((error) => {
      logError('forwardGeocoding', error);
      errorCallback(error);
    });
}

export { forwardGeocoding as default };
